import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Checkbox,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextContent,
  Toggle,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationAttemptKind,
  InvestigationRedriveAttempt,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  RETURNED_ITEMS_PAGE_SIZE,
  useInvestigationRedrives,
  useInvestigationReturnedItems,
  fetchAllReturnedPks,
  usePopulationRefreshProgress,
  useRefreshPopulation,
  useConfirmDocumentRedrive,
  useStartDocumentRedrive,
  useStartRedrive,
  useUninvestigatedGroups,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import { monitoredAttempt } from "./MonitoringRedriveWatch";
import {
  HUMAN_TRANSITIONS,
  STATUS_LABELS,
  formatSnapshotTimestamp,
  formatTimestamp,
  investigationPath,
  isTerminal,
  s3ConsoleUrl,
} from "./investigationUtils";
import { TransactionLink } from "./TransactionLink";
import { DlqPkCell } from "./DlqEventModal";
import { InvestigationDeletionFlow } from "./InvestigationDeletionFlow";
import { RedriveOptionId, RedriveOptionsModal } from "./RedriveOptionsModal";
import { ExternalRedriveModal } from "./ExternalRedriveModal";
import { ReminderNote, RemindMeButton } from "./RemindMeButton";
import { BackToCbeCheck } from "./BackToCbeCheck";
import { RedriveMonitoringSection } from "./RedriveMonitoringSection";
import {
  awaitingConfirmation,
  documentRedriveSummary,
  readyForDlq,
} from "./documentRedrive";
import { SafeExternalLink } from "./SafeExternalLink";

interface InvestigationRedrivePanelProps {
  stage: Stage;
  investigation: Investigation;
  currentUser: string | null;
  onError: (message: string) => void;
  /** Which half to render: the Population tab shows the frozen population, the Redrives tab
   * the step-2 card, deletion flow, attempts and launch modals. Default: both. */
  section?: "population" | "redrives" | "all";
  /** Opens Split by list with these DLQ keys (owner only); no split action when unset. */
  onSplitItems?: (pks: string[], itemsLabel: string) => void;
  /** Why splitting is unavailable (shown on the disabled button). */
  splitDisabledReason?: string;
}

const isDeletion = (attempt: InvestigationRedriveAttempt) =>
  attempt.kind === "DELETE";

const isDocumentRedrive = (attempt: InvestigationRedriveAttempt) =>
  attempt.kind === "DOCUMENT_REDRIVE";

const attemptStatus = (attempt: InvestigationRedriveAttempt) => {
  switch (attempt.status) {
    case "COMPLETED":
      if (isDeletion(attempt)) {
        if (attempt.verification === "INCOMPLETE") {
          return (
            <StatusIndicator type="warning">
              Completed, {attempt.remainingCount ?? 0} still in DLQ
            </StatusIndicator>
          );
        }
        return (
          <StatusIndicator
            type={attempt.verification === "VERIFIED" ? "success" : "pending"}
          >
            {attempt.verification === "VERIFIED"
              ? "Completed, verified absent"
              : "Completed, awaiting snapshot verification"}
          </StatusIndicator>
        );
      }
      // A later snapshot still showed some of its items untouched: never requeued.
      if (attempt.verification === "INCOMPLETE") {
        return (
          <StatusIndicator type="warning">
            Completed, {attempt.remainingCount ?? 0} not redriven
          </StatusIndicator>
        );
      }
      return (
        <StatusIndicator type={attempt.hasReturns ? "warning" : "success"}>
          {attempt.hasReturns ? "Completed, returns detected" : "Completed"}
        </StatusIndicator>
      );
    case "FAILED":
      return <StatusIndicator type="error">Failed</StatusIndicator>;
    case "AWAITING_APPROVAL":
      return (
        <StatusIndicator type="pending">Awaiting MCM approval</StatusIndicator>
      );
    case "CANCELLED":
      return <StatusIndicator type="stopped">Cancelled</StatusIndicator>;
    default:
      return (
        <StatusIndicator type="in-progress">{attempt.status}</StatusIndicator>
      );
  }
};

// Same wording as the standalone redrive tool's "Type" column.
const attemptType = (a: InvestigationRedriveAttempt) => {
  if (a.dryRun) return "Dry run";
  if (isDeletion(a)) return "Delete without redrive";
  if (isDocumentRedrive(a)) return "Order redrive";
  return "Redrive";
};

const renderMcm = (a: InvestigationRedriveAttempt) =>
  a.mcmId ? (
    <SafeExternalLink
      href={a.mcmUrl}
      externalIconAriaLabel="Opens MCM in a new tab"
    >
      {a.mcmId}
    </SafeExternalLink>
  ) : (
    "—"
  );

// A deletion request that has not run yet lives in the Step 1 / Step 2 flow above the
// table; listing it here as well only duplicates it.
const isUnexecutedDeletion = (a: InvestigationRedriveAttempt) =>
  isDeletion(a) &&
  (a.status === "AWAITING_APPROVAL" || (a.status === "FAILED" && !a.startedAt));

// The redriver reports a per-message result (SUCCESS / ERROR / DRY_RUN); a order redrive
// reports one per transaction (DOCUMENT_SUCCESS / DOCUMENT_FAILED, BATCH_FAILED = never ran).
const OUTCOME_OK = new Set(["SUCCESS", "DOCUMENT_SUCCESS"]);
const outcomeText = (
  a: InvestigationRedriveAttempt,
  key: string,
  n: number
) => {
  if (key === "DOCUMENT_SUCCESS") return `${n} redriven`;
  if (key === "BATCH_FAILED") return `${n} not run`;
  if (key === "SUCCESS")
    return `${n} ${isDeletion(a) ? "deleted" : "requeued"}`;
  if (key === "DRY_RUN") return `${n} validated (dry run)`;
  return `${n} failed`;
};

const renderOutcomes = (a: InvestigationRedriveAttempt) => {
  const entries = Object.entries(a.outcomes || {}).filter(
    ([, v]) => Number(v) > 0
  );
  if (!entries.length) return "—";
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      {entries.map(([k, v]) => (
        <StatusIndicator
          key={k}
          type={
            OUTCOME_OK.has(k) ? "success" : k === "DRY_RUN" ? "info" : "error"
          }
        >
          {outcomeText(a, k, Number(v))}
        </StatusIndicator>
      ))}
    </SpaceBetween>
  );
};

const renderReturned = (a: InvestigationRedriveAttempt) => {
  // The DLQ is untouched by a order redrive, so nothing can return from it.
  if (isDocumentRedrive(a)) return "—";
  const returned = a.returnedCount ?? 0;
  // Relative to what actually went back onto the queue; fall back to processed.
  const base = Number(a.outcomes?.SUCCESS ?? a.processedCount ?? 0);
  const pct = base > 0 ? Math.min(100, Math.round((returned / base) * 100)) : 0;
  if (!returned) return base > 0 ? "0 (0%)" : "0";
  return (
    <Badge color="red">{base > 0 ? `${returned} (${pct}%)` : returned}</Badge>
  );
};

/** One row per returned DLQ message. */
interface ReturnedRow {
  dlqPk?: string;
  transactionId?: string;
  orderId?: string;
  attemptId?: string;
  detectedAt?: string;
}

const returnedTxnCell = (r: ReturnedRow, stage: Stage) =>
  r.transactionId || r.orderId ? (
    <TransactionLink
      stage={stage}
      transactionId={r.transactionId}
      orderId={r.orderId}
    />
  ) : (
    "—"
  );

const returnedPkCell = (r: ReturnedRow, stage: Stage) => (
  <DlqPkCell
    stage={stage}
    dlqPk={r.dlqPk}
    transactionId={r.transactionId}
    orderId={r.orderId}
  />
);

/**
 * Every returned message, server-paginated (a big redrive can return 100k+). Returns recorded
 * before the paginated index existed only live inline on their attempt, so those are shown
 * when the index has nothing for this investigation.
 */
const ReturnedItemsTable: React.FC<{
  stage: Stage;
  investigationId: string;
  attempts: InvestigationRedriveAttempt[];
  totalReturned: number;
  onSplit?: (pks: string[], itemsLabel: string) => void;
  splitDisabledReason?: string;
}> = ({
  stage,
  investigationId,
  attempts,
  totalReturned,
  onSplit,
  splitDisabledReason,
}) => {
  const [pageIndex, setPageIndex] = useState(1);
  const [selected, setSelected] = useState<ReturnedRow[]>([]);
  const [collecting, setCollecting] = useState(false);
  const query = useInvestigationReturnedItems(
    stage,
    investigationId,
    totalReturned
  );
  const pages = query.data?.pages ?? [];
  const indexed = pages.some((p) => (p.items || []).length);
  const legacy: ReturnedRow[] = attempts.flatMap(
    (a) =>
      (a.returnedItems || []).map((r) => ({
        ...r,
        attemptId: a.attemptId ?? undefined,
      })) as ReturnedRow[]
  );
  const usingLegacy = !query.isLoading && !indexed && legacy.length > 0;
  const loadedPages: ReturnedRow[][] = usingLegacy
    ? Array.from(
        { length: Math.ceil(legacy.length / RETURNED_ITEMS_PAGE_SIZE) },
        (_, i) =>
          legacy.slice(
            i * RETURNED_ITEMS_PAGE_SIZE,
            (i + 1) * RETURNED_ITEMS_PAGE_SIZE
          )
      )
    : pages.map((p) => (p.items || []) as ReturnedRow[]);
  // The total is known up front, so show every page number rather than an open-ended "1 …".
  const knownPages = usingLegacy
    ? 0
    : Math.ceil(totalReturned / RETURNED_ITEMS_PAGE_SIZE);
  const pagesCount = Math.max(1, loadedPages.length, knownPages);
  const current = Math.min(pageIndex, pagesCount);
  const hasMore = !usingLegacy && !!query.hasNextPage;
  // Only open-ended if the server has more than the recorded total suggests.
  const openEnd = hasMore && loadedPages.length >= pagesCount;

  // Cursor pagination: jumping ahead loads the intervening pages in order.
  const loadThrough = (next: number, loaded: number, more: boolean) => {
    if (next <= loaded || !more) {
      setPageIndex(next);
      return;
    }
    query
      .fetchNextPage()
      .then((res) => {
        const n = res?.data?.pages.length;
        if (res?.isError || !n || n <= loaded) setPageIndex(next);
        else loadThrough(next, n, !!res.hasNextPage);
      })
      .catch(() => setPageIndex(next)); // the query surfaces its own error state
  };
  const goTo = (next: number) => loadThrough(next, loadedPages.length, hasMore);
  const rowKey = (r: ReturnedRow) => `${r.attemptId ?? ""}-${r.dlqPk ?? ""}`;
  const pageRows = loadedPages[current - 1] ?? [];
  const pksOf = (rows: ReturnedRow[]) => [
    ...new Set(rows.map((r) => r.dlqPk).filter((pk): pk is string => !!pk)),
  ];

  // "Split all": read every key in the background (large pages, outside the table's query), so
  // the table keeps showing the current page while the button spins.
  const splitAll = async () => {
    if (!onSplit) return;
    if (usingLegacy) {
      onSplit(pksOf(loadedPages.flat()), "returned items");
      return;
    }
    setCollecting(true);
    try {
      onSplit(
        await fetchAllReturnedPks(stage, investigationId),
        "returned items"
      );
    } finally {
      setCollecting(false);
    }
  };

  return (
    <Table
      variant="container"
      items={pageRows}
      trackBy={rowKey}
      loading={query.isLoading || query.isFetchingNextPage}
      loadingText="Loading returned items"
      selectionType={onSplit ? "multi" : undefined}
      selectedItems={selected}
      onSelectionChange={({ detail }) => {
        // Keep ticks made on other pages; only this page's rows are toggled here.
        const visible = new Set(pageRows.map(rowKey));
        setSelected([
          ...selected.filter((r) => !visible.has(rowKey(r))),
          ...detail.selectedItems.filter((r) => visible.has(rowKey(r))),
        ]);
      }}
      ariaLabels={{
        selectionGroupLabel: "Returned items",
        allItemsSelectionLabel: () => "select all on this page",
        itemSelectionLabel: (_, r) => r.dlqPk ?? "",
      }}
      header={
        <Header
          variant="h3"
          counter={
            selected.length
              ? `(${selected.length}/${totalReturned})`
              : `(${totalReturned})`
          }
          actions={
            onSplit && (
              <Button
                iconName="expand"
                iconAlign="right"
                loading={collecting}
                disabled={!!splitDisabledReason}
                disabledReason={splitDisabledReason}
                onClick={() => {
                  if (selected.length)
                    onSplit(pksOf(selected), "returned items");
                  else splitAll().catch(console.error);
                }}
              >
                {selected.length
                  ? `Split ${selected.length} selected`
                  : `Split all ${totalReturned.toLocaleString()} returned`}
              </Button>
            )
          }
        >
          Returned items
        </Header>
      }
      columnDefinitions={[
        {
          id: "transaction",
          header: "Transaction",
          cell: (r) => returnedTxnCell(r, stage),
        },
        {
          id: "dlqPk",
          header: "DLQ message",
          cell: (r) => returnedPkCell(r, stage),
        },
        {
          id: "detectedAt",
          header: "Returned",
          cell: (r) => formatTimestamp(r.detectedAt),
        },
      ]}
      pagination={
        pagesCount > 1 || openEnd ? (
          <Pagination
            currentPageIndex={current}
            pagesCount={pagesCount}
            openEnd={openEnd}
            onChange={({ detail }) => goTo(detail.currentPageIndex)}
            onNextPageClick={({ detail }) => {
              if (!detail.requestedPageAvailable)
                goTo(detail.requestedPageIndex);
            }}
          />
        ) : undefined
      }
      empty={<Box textAlign="center">No returned items</Box>}
    />
  );
};

/** One row per transaction an order redrive could not redrive. */
interface FailedRedriveRow {
  transactionId: string;
  attemptId?: string;
  startedAt?: string | null;
}

const failedTxnCell = (r: FailedRedriveRow, stage: Stage) => (
  <TransactionLink stage={stage} transactionId={r.transactionId} />
);

/**
 * Transactions an order redrive could not redrive (neither the accounting group nor the
 * document), capped server-side at 100 per attempt. Client-side paginated like the other
 * redrive result tables.
 */
const FailedRedrivesTable: React.FC<{
  stage: Stage;
  attempts: InvestigationRedriveAttempt[];
}> = ({ stage, attempts }) => {
  const [pageIndex, setPageIndex] = useState(1);
  const rows: FailedRedriveRow[] = attempts.flatMap((a) =>
    (a.failedIds || []).map((id) => ({
      transactionId: id,
      attemptId: a.attemptId ?? undefined,
      startedAt: a.startedAt,
    }))
  );
  const pagesCount = Math.max(
    1,
    Math.ceil(rows.length / RETURNED_ITEMS_PAGE_SIZE)
  );
  const current = Math.min(pageIndex, pagesCount);
  return (
    <Table
      variant="container"
      items={rows.slice(
        (current - 1) * RETURNED_ITEMS_PAGE_SIZE,
        current * RETURNED_ITEMS_PAGE_SIZE
      )}
      trackBy={(r) => `${r.attemptId ?? ""}-${r.transactionId}`}
      header={
        <Header
          variant="h3"
          counter={`(${rows.length})`}
          description="Neither the accounting group nor the document could be redriven (first 100 per attempt)."
        >
          Failed order redrives
        </Header>
      }
      columnDefinitions={[
        {
          id: "transaction",
          header: "Transaction",
          cell: (r) => failedTxnCell(r, stage),
        },
        {
          id: "attempt",
          header: "Attempt started",
          cell: (r) => formatTimestamp(r.startedAt),
        },
      ]}
      pagination={
        pagesCount > 1 ? (
          <Pagination
            currentPageIndex={current}
            pagesCount={pagesCount}
            onChange={({ detail }) => setPageIndex(detail.currentPageIndex)}
          />
        ) : undefined
      }
      empty={<Box textAlign="center">No failed transactions</Box>}
    />
  );
};

/**
 * Link to the attempt's execution on the Lusca Search redrive pages: the DLQ Redrive page for
 * DLQ redrives and deletions, the Redrive Tool page for order redrives. Stages this
 * deployment cannot redrive (redriveMode external) live on the Prod Lusca Search site.
 */
export const executionPageUrl = (
  a: InvestigationRedriveAttempt,
  stage: string,
  externalBaseUrl?: string
): string | undefined => {
  if (!a.executionArn) return undefined;
  const base = (externalBaseUrl ?? "").replace(/\/+$/, "");
  const query = `stage=${encodeURIComponent(stage)}`;
  if (isDocumentRedrive(a)) {
    const executionId = a.executionArn.split(":").pop() ?? a.executionArn;
    return `${base}/redrive?${query}&executionId=${encodeURIComponent(
      executionId
    )}`;
  }
  return `${base}/dlq-redrive?${query}&executionArn=${encodeURIComponent(
    a.executionArn
  )}`;
};

const renderExecution = (
  a: InvestigationRedriveAttempt,
  stage: string,
  externalBaseUrl?: string
) => {
  const url = executionPageUrl(a, stage, externalBaseUrl);
  return url ? (
    <Link href={url} external>
      View execution
    </Link>
  ) : (
    "—"
  );
};

/**
 * Frozen population, redrive launch (owner only, READY_TO_REDRIVE), the gating
 * explanation with an owner-only "Mark ready to redrive" shortcut, and attempt history.
 */
export const InvestigationRedrivePanel: React.FC<
  InvestigationRedrivePanelProps
> = ({
  stage,
  investigation,
  currentUser,
  onError,
  section = "all",
  onSplitItems,
  splitDisabledReason,
}) => {
  const showPopulation = section !== "redrives";
  // With zero attempts the query stays disabled; a disabled TanStack v4 query reports
  // isLoading=true forever, so gate the table spinner on hasAttempts.
  const hasAttempts =
    (investigation.redriveAttemptCount ?? 0) > 0 ||
    (investigation.documentRedriveCount ?? 0) > 0;
  const redrives = useInvestigationRedrives(
    stage,
    investigation.id,
    hasAttempts
  );
  const start = useStartRedrive(stage);
  const startDocument = useStartDocumentRedrive(stage);
  const [confirmDocument, setConfirmDocument] = useState(false);
  // Hourly reconciliation time = when Lusca last checked for returns (shared cache with the
  // Overview, not an extra call).
  const groups = useUninvestigatedGroups(stage, false);
  const refresh = useRefreshPopulation(stage);
  // Preview id of the refresh started (or found already running) in this session, so the
  // progress indicator and the outcome banner appear even before the detail refetch lands.
  const [refreshPreviewId, setRefreshPreviewId] = useState<string | null>(null);
  const [outcomeDismissed, setOutcomeDismissed] = useState(false);
  const refreshProgress = usePopulationRefreshProgress(
    stage,
    investigation,
    refreshPreviewId
  );
  const startRefresh = () => {
    setOutcomeDismissed(false);
    refresh
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
      })
      .then((preview) => setRefreshPreviewId(preview.previewId))
      .catch((err: InvestigationApiError) => {
        if (err.code === "REFRESH_IN_PROGRESS" && err.preview) {
          // Someone (or another tab) already started one: follow it instead of failing.
          setRefreshPreviewId(err.preview.previewId);
          return;
        }
        onError(err.message);
      });
  };
  const refreshOutcome = outcomeDismissed ? undefined : refreshProgress.settled;
  const update = useUpdateInvestigation(stage);
  const [confirm, setConfirm] = useState(false);
  const [confirmReady, setConfirmReady] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const isOwner = !!currentUser && investigation.owner === currentUser;
  const status = investigation.status ?? "";
  const isReady = status === "READY_TO_REDRIVE";
  const inRedriveCycle =
    status === "EXECUTING_REDRIVE" || status === "MONITORING_REDRIVE";
  const deletionPending = !!investigation.pendingDeletion;
  const doc = investigation.documentRedrive;
  const needsCbeCheck = awaitingConfirmation(investigation);
  const onDlqStep = readyForDlq(status, doc);
  const canLaunch = isOwner && isReady && !deletionPending && !needsCbeCheck;
  // Monitoring is past the redrive step: the natural next action is Resolve (returns reopen the
  // case automatically), so the panel does not promote "mark ready" there — a second run is
  // still reachable via "Redrive again" in the Status card.
  const canMarkReady =
    isOwner &&
    status !== "MONITORING_REDRIVE" &&
    (HUMAN_TRANSITIONS[status] || []).includes("READY_TO_REDRIVE");
  const attempts = redrives.data?.attempts || [];
  const executedAttempts = attempts.filter((a) => !isUnexecutedDeletion(a));
  // The latest redrive left items in the DLQ untouched; the worker moved the case back to
  // Ready to redrive, so the next step is simply to redrive again.
  const notRedriven = isReady
    ? executedAttempts.find(
        (a) =>
          a.attemptId === investigation.latestRedrive?.attemptId &&
          !isDeletion(a) &&
          a.verification === "INCOMPLETE"
      )
    : undefined;
  // Attempts a merged-away case launched stay on its record; the survivor lists them, tagged.
  const renderSource = (a: InvestigationRedriveAttempt) =>
    a.sourceInvestigationId ? (
      <Link href={investigationPath(a.sourceInvestigationId, stage)}>
        merged case
      </Link>
    ) : (
      "this case"
    );

  const ownerLabel = `the owner${
    investigation.owner ? ` (${investigation.owner})` : ""
  }`;
  const statusLabel = STATUS_LABELS[status] || status;

  const describeLaunchBlock = (): string | undefined => {
    if (!isOwner) return `Only ${ownerLabel} can launch a redrive.`;
    if (!isReady) {
      return `The investigation must be in ${STATUS_LABELS.READY_TO_REDRIVE} first (currently ${statusLabel}).`;
    }
    if (deletionPending) {
      return "A delete-without-redrive request is awaiting MCM approval; cancel it before redriving.";
    }
    if (needsCbeCheck) {
      return "Check the CBEs first (Check CBEs, above).";
    }
    return undefined;
  };
  const launchDisabledReason = describeLaunchBlock();

  // Delete and order redrive share the DLQ redrive's gate, minus the CBE check.
  const describeReadyBlock = (verb: string): string | undefined => {
    if (!isOwner) return `Only ${ownerLabel} can ${verb}.`;
    if (!isReady) {
      return `The investigation must be in ${STATUS_LABELS.READY_TO_REDRIVE} first (currently ${statusLabel}).`;
    }
    if (deletionPending) {
      return "A delete-without-redrive request is awaiting MCM approval; cancel it first.";
    }
    return undefined;
  };
  const optionDisabledReason = (id: RedriveOptionId): string | undefined => {
    if (id === "dlq") return launchDisabledReason;
    if (id === "delete") return describeReadyBlock("request a deletion");
    return describeReadyBlock("launch a order redrive");
  };
  const [optionsOpen, setOptionsOpen] = useState(false);
  // `?redrive=choose` (after "Mark ready" / "Redrive again" on the Status card) opens the
  // chooser; `?redrive=check` opens the CBE check in a modal, `?redrive=clear` the DLQ redrive.
  const [searchParams, setSearchParams] = useSearchParams();
  const redriveAction = searchParams.get("redrive");
  const [step2Open, setStep2Open] = useState(false);
  useEffect(() => {
    if (!redriveAction) return;
    if (redriveAction === "choose" && isOwner && isReady && !onDlqStep) {
      setOptionsOpen(true);
    }
    // "check" (Status card's Check CBEs step) opens the CBE check; "clear" (its DLQ redrive
    // step) goes straight to the DLQ redrive dialog once the CBEs are confirmed.
    if (
      (redriveAction === "check" || redriveAction === "clear") &&
      onDlqStep &&
      doc
    ) {
      if (redriveAction === "clear" && !needsCbeCheck && isOwner)
        // Defined below with the other dialog openers; the effect runs after render.
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        openDlqRedrive();
      else setStep2Open(true);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("redrive");
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- act once per request
  }, [redriveAction]);
  const [deletionOpen, setDeletionOpen] = useState(false);
  // Prod from a non-prod deployment: redrives run in Lusca Search Prod and are linked back.
  const external = investigation.redriveMode === "external";
  const [externalKind, setExternalKind] =
    useState<InvestigationAttemptKind | null>(null);
  const EXTERNAL_KIND: Record<RedriveOptionId, InvestigationAttemptKind> = {
    dlq: "REDRIVE",
    document: "DOCUMENT_REDRIVE",
    delete: "DELETE",
  };
  const openDlqRedrive = () =>
    external ? setExternalKind("REDRIVE") : setConfirm(true);
  const chooseOption = (id: RedriveOptionId) => {
    if (external) {
      setExternalKind(EXTERNAL_KIND[id]);
      return;
    }
    if (id === "dlq") setConfirm(true);
    if (id === "delete") setDeletionOpen(true);
    if (id === "document") setConfirmDocument(true);
  };

  const confirmCbeCheck = useConfirmDocumentRedrive(stage);
  const [cbesChecked, setCbesChecked] = useState(false);
  const confirmCbes = () =>
    confirmCbeCheck
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
        attemptId: doc?.attemptId ?? "",
      })
      .then(() => {
        setCbesChecked(false);
        return true;
      })
      .catch((err: InvestigationApiError) => {
        onError(err.message);
        return false;
      });

  const describeReadyHint = (): string => {
    if (!isOwner) return `Only ${ownerLabel} can mark it ready to redrive.`;
    if (canMarkReady) {
      return "Once the fix is in place, mark it ready to unlock the redrive controls.";
    }
    if (isTerminal(status)) {
      return "Reopen the investigation before it can be made ready to redrive.";
    }
    return "";
  };
  const readyHint = describeReadyHint();

  const markReady = () =>
    update
      .mutateAsync({
        investigationId: investigation.id,
        request: {
          expectedVersion: investigation.version,
          status: "READY_TO_REDRIVE",
        },
      })
      .then(() => setConfirmReady(false))
      .catch((err: InvestigationApiError) => onError(err.message));

  const manifestConsoleUrl = s3ConsoleUrl(
    investigation.population?.manifestUri
  );

  const launch = () =>
    start
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
        dryRun,
      })
      .then(() => setConfirm(false))
      .catch((err: InvestigationApiError) => onError(err.message));

  const launchDocument = () =>
    startDocument
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
      })
      .then(() => setConfirmDocument(false))
      .catch((err: InvestigationApiError) => onError(err.message));

  const failedDocuments = attempts.filter(
    (a) => isDocumentRedrive(a) && (a.failedIds || []).length
  );

  // Shared by the inline card and the modal the Status card's "Check CBEs" step opens. Checking
  // the CBEs and clearing the DLQ are separate steps: confirming only confirms (the case then
  // shows the DLQ redrive as next); opening the DLQ redrive dialog closes this modal.
  const runDlqRedrive = () => {
    setStep2Open(false);
    openDlqRedrive();
  };
  const step2Body = doc && (
    <SpaceBetween size="xs">
      {needsCbeCheck ? (
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <span>Check a few:</span>
          {(doc.sampleIds || []).map((id) => (
            <TransactionLink key={id} stage={stage} transactionId={id} />
          ))}
          {isOwner ? (
            <>
              <Checkbox
                checked={cbesChecked}
                onChange={({ detail }) => setCbesChecked(detail.checked)}
              >
                CBEs look right
              </Checkbox>
              <Button
                variant="primary"
                disabled={!cbesChecked}
                loading={confirmCbeCheck.isLoading}
                onClick={() => {
                  confirmCbes()
                    .then((ok) => ok && setStep2Open(false))
                    .catch(console.error);
                }}
              >
                Confirm
              </Button>
              <RemindMeButton
                stage={stage}
                investigation={investigation}
                onError={onError}
              />
              <ReminderNote investigation={investigation} />
            </>
          ) : (
            <Box color="text-body-secondary">
              Only {ownerLabel} can confirm.
            </Box>
          )}
        </SpaceBetween>
      ) : (
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <StatusIndicator type="success">
            CBEs confirmed by {doc.confirmedBy ?? "the owner"}
          </StatusIndicator>
          {isOwner && (
            <BackToCbeCheck
              stage={stage}
              investigation={investigation}
              onError={onError}
            />
          )}
          <Button
            variant="primary"
            disabled={!canLaunch}
            disabledReason={launchDisabledReason}
            onClick={runDlqRedrive}
          >
            Run DLQ redrive
          </Button>
        </SpaceBetween>
      )}
      {!!doc.failedCount && (
        <Box color="text-status-warning" fontSize="body-s">
          {doc.failedCount} transaction(s) failed to redrive; their DLQ items
          will probably fail again.
        </Box>
      )}
    </SpaceBetween>
  );
  const step2Title = needsCbeCheck ? "Check CBEs" : "Clear the DLQ";
  const step2Summary = doc
    ? documentRedriveSummary(doc) || "Order redrive finished"
    : "";

  const populationCard = (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {manifestConsoleUrl && (
                <Button
                  iconName="external"
                  iconAlign="right"
                  href={manifestConsoleUrl}
                  target="_blank"
                >
                  Frozen manifest in S3
                </Button>
              )}
              {isOwner && !isTerminal(investigation.status) && (
                <Button
                  loading={refresh.isLoading}
                  disabled={refreshProgress.inProgress}
                  disabledReason="A refresh is already running; wait for it to finish before starting another one."
                  onClick={startRefresh}
                >
                  Refresh population
                </Button>
              )}
            </SpaceBetween>
          }
        >
          Frozen population
        </Header>
      }
    >
      <SpaceBetween size="xs">
        {refreshProgress.inProgress && (
          <Alert type="info" header="Refreshing population">
            <SpaceBetween size="xxs">
              <StatusIndicator type="in-progress">
                Re-running the rule against the latest DLQ snapshot
                {refreshProgress.startedAt
                  ? ` (started ${formatTimestamp(refreshProgress.startedAt)}${
                      refreshProgress.startedBy
                        ? ` by ${refreshProgress.startedBy}`
                        : ""
                    })`
                  : ""}
                …
              </StatusIndicator>
              <span>
                This scans Athena once and can take a minute or two; the frozen
                population below updates automatically when it finishes.
                Starting another refresh is blocked until then.
              </span>
            </SpaceBetween>
          </Alert>
        )}
        {refreshOutcome && (
          <Alert
            type={refreshOutcome.status === "FAILED" ? "error" : "success"}
            dismissible
            dismissAriaLabel="Dismiss refresh result"
            onDismiss={() => setOutcomeDismissed(true)}
            header={
              refreshOutcome.status === "FAILED"
                ? "Population refresh failed"
                : "Population refreshed"
            }
          >
            {refreshOutcome.status === "FAILED"
              ? refreshOutcome.error ||
                "The snapshot query failed. You can try again."
              : `${
                  refreshOutcome.count ?? 0
                } item(s) match the rule in the latest snapshot; newly matching items were assigned and the frozen manifest was replaced.`}
          </Alert>
        )}
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: "Frozen",
              value: `${
                investigation.population?.count ?? 0
              } items · ${formatTimestamp(investigation.population?.frozenAt)}`,
            },
            {
              label: "From snapshot",
              value: formatSnapshotTimestamp(
                investigation.population?.snapshotTimestamp
              ),
            },
            {
              label: "Currently assigned",
              value: `${investigation.assignedCount ?? 0}`,
            },
            {
              label: "Frozen manifest",
              value: investigation.population?.manifestUri ? (
                <SpaceBetween size="xxs">
                  <Box variant="code" display="inline-block">
                    <span style={{ overflowWrap: "anywhere" }}>
                      {manifestConsoleUrl ? (
                        <Link
                          href={manifestConsoleUrl}
                          external
                          variant="primary"
                        >
                          {investigation.population.manifestUri}
                        </Link>
                      ) : (
                        investigation.population.manifestUri
                      )}
                    </span>
                  </Box>
                  {investigation.population?.checksum && (
                    <Box color="text-body-secondary" fontSize="body-s">
                      sha256 {investigation.population.checksum.slice(0, 12)}…
                    </Box>
                  )}
                </SpaceBetween>
              ) : (
                "—"
              ),
            },
          ]}
        />
        <Box color="text-body-secondary" fontSize="body-s">
          New snapshot items join automatically only when exactly one active
          rule (see Overview) matches; redrives always use this exact frozen
          manifest.
        </Box>
        {investigation.populationOversized && (
          <Alert type="warning" header="Live rule matches too many items">
            The rule matches {investigation.liveMatchCount ?? "many"} snapshot
            rows, more than the reconciliation can assign exactly. Narrow the
            rule (split out a narrower case) so new items join automatically.
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
  // Population tab: only the frozen population (the trend chart sits above it).
  if (section === "population") return populationCard;

  return (
    <SpaceBetween size="m">
      {onDlqStep && doc && (
        <Modal
          visible={step2Open}
          onDismiss={() => setStep2Open(false)}
          header={step2Title}
          size="large"
        >
          <div data-testid="document-redrive-step2-modal">
            <SpaceBetween size="xs">
              <Box color="text-body-secondary">{step2Summary}</Box>
              {step2Body}
            </SpaceBetween>
          </div>
        </Modal>
      )}
      {onDlqStep && doc && (
        <Container data-testid="document-redrive-step2">
          <SpaceBetween size="xs">
            <SpaceBetween direction="horizontal" size="s" alignItems="center">
              <Box variant="h3" padding="n">
                {step2Title}
              </Box>
              <Box color="text-body-secondary">{step2Summary}</Box>
            </SpaceBetween>
            {step2Body}
          </SpaceBetween>
        </Container>
      )}
      {showPopulation && populationCard}

      {deletionPending && (
        <InvestigationDeletionFlow
          stage={stage}
          investigation={investigation}
          attempts={attempts}
          isOwner={isOwner}
          onError={onError}
        />
      )}

      {status === "MONITORING_REDRIVE" && (
        <RedriveMonitoringSection
          stage={stage}
          investigation={investigation}
          attempt={monitoredAttempt(investigation, redrives?.data?.attempts)}
          reconciledAt={groups?.data?.reconciledAt}
          currentUser={currentUser}
          onError={onError}
        />
      )}

      <Container
        header={
          <Header
            variant="h3"
            counter={`(${executedAttempts.length})`}
            description={
              !isReady && !inRedriveCycle && !deletionPending
                ? readyHint || undefined
                : undefined
            }
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {!deletionPending && (
                  <InvestigationDeletionFlow
                    stage={stage}
                    investigation={investigation}
                    attempts={attempts}
                    isOwner={isOwner}
                    onError={onError}
                    requestOpen={deletionOpen}
                    onRequestOpenChange={setDeletionOpen}
                  />
                )}
                {/* One next action: mark ready first, then choose how to redrive. */}
                {!isReady && canMarkReady ? (
                  <Button
                    variant="primary"
                    onClick={() => setConfirmReady(true)}
                  >
                    Mark ready to redrive
                  </Button>
                ) : (
                  !inRedriveCycle && (
                    <Button
                      variant="primary"
                      data-testid="redrive-options"
                      ariaLabel="Choose how to redrive (opens a dialog)"
                      disabled={!isOwner || !isReady}
                      disabledReason={
                        !isOwner
                          ? `Only ${ownerLabel} can redrive.`
                          : readyHint || undefined
                      }
                      onClick={() => setOptionsOpen(true)}
                    >
                      Choose how to redrive…
                    </Button>
                  )
                )}
              </SpaceBetween>
            }
          >
            Redrive attempts
          </Header>
        }
      >
        <SpaceBetween size="s">
          {status === "EXECUTING_REDRIVE" && (
            // The system is working: a quiet line, not a banner.
            <Box color="text-body-secondary" data-testid="redrive-cycle-status">
              <StatusIndicator type="loading">
                Redrive running — the status moves on when it completes.
              </StatusIndicator>
            </Box>
          )}
          {notRedriven && (
            // One quiet line; the redrive button in the header is the action.
            <Box data-testid="redrive-not-redriven">
              <StatusIndicator type="warning">
                {notRedriven.remainingCount ?? 0} item
                {notRedriven.remainingCount === 1 ? " was" : "s were"} not
                redriven and {notRedriven.remainingCount === 1 ? "is" : "are"}{" "}
                still in the DLQ — redrive again.
              </StatusIndicator>
            </Box>
          )}
          {!!investigation.returnedCount && (
            <Alert type="warning">
              {investigation.returnedCount} redriven message(s) returned to the
              DLQ. Returned items are linked to their attempt below and the case
              was reopened.
            </Alert>
          )}
          <Table
            variant="embedded"
            items={executedAttempts}
            loading={hasAttempts && redrives.isLoading}
            columnDefinitions={[
              // Execution leads: it is the attempt's handle (the opaque att- id is not shown).
              {
                id: "execution",
                header: "Execution",
                cell: (a) =>
                  renderExecution(
                    a,
                    stage,
                    external ? investigation.externalRedriveUrl : undefined
                  ),
              },
              ...(executedAttempts.some((a) => a.sourceInvestigationId)
                ? [
                    {
                      id: "from",
                      header: "From",
                      cell: renderSource,
                    },
                  ]
                : []),
              { id: "type", header: "Type", cell: attemptType },
              { id: "status", header: "Status", cell: (a) => attemptStatus(a) },
              {
                id: "requested",
                header: "Requested",
                cell: (a) => a.requestedCount ?? 0,
              },
              {
                id: "processed",
                header: "Processed",
                cell: (a) => a.processedCount ?? 0,
              },
              { id: "outcomes", header: "Result", cell: renderOutcomes },
              { id: "returns", header: "Returned", cell: renderReturned },
              {
                id: "actor",
                header: "Actor",
                cell: (a) => `${a.actor ?? ""} (${a.source ?? ""})`,
              },
              {
                id: "started",
                header: "Started",
                cell: (a) => formatTimestamp(a.startedAt),
              },
              { id: "mcm", header: "MCM", cell: renderMcm },
            ]}
            empty={<Box textAlign="center">No redrive attempts yet</Box>}
          />
          {failedDocuments.length > 0 && (
            <FailedRedrivesTable stage={stage} attempts={failedDocuments} />
          )}
          {(!!investigation.returnedCount ||
            attempts.some((a) => (a.returnedItems || []).length)) && (
            <ReturnedItemsTable
              stage={stage}
              investigationId={investigation.id}
              attempts={attempts}
              onSplit={onSplitItems}
              splitDisabledReason={splitDisabledReason}
              totalReturned={
                investigation.returnedCount ??
                attempts.reduce((n, a) => n + (a.returnedCount ?? 0), 0)
              }
            />
          )}
        </SpaceBetween>
      </Container>

      <Modal
        visible={confirm}
        onDismiss={() => setConfirm(false)}
        header={`Redrive ${investigation.assignedCount ?? 0} ${
          investigation.assignedCount === 1 ? "item" : "items"
        }?`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  launch().catch(console.error);
                }}
                loading={start.isLoading}
              >
                {dryRun ? "Launch dry run" : "Launch redrive"}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <TextContent>
            <p>
              Requeues exactly the items owned now. Items that join later are
              not included.
            </p>
          </TextContent>
          <Toggle
            checked={dryRun}
            onChange={({ detail }) => setDryRun(detail.checked)}
          >
            Dry run
          </Toggle>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={confirmDocument}
        onDismiss={() => setConfirmDocument(false)}
        header="Order redrive these transactions?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmDocument(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  launchDocument().catch(console.error);
                }}
                loading={startDocument.isLoading}
              >
                Launch order redrive
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          <p>
            Sends the distinct transactions of the{" "}
            {investigation.assignedCount ?? 0} items owned now to the order
            redrive. Lusca rebuilds each document (new version) and republishes
            its CBEs.
          </p>
          <p>
            The DLQ items stay in the DLQ. When it finishes, check the CBEs were
            republished, then run the DLQ redrive to clear them.
          </p>
        </TextContent>
      </Modal>

      <ExternalRedriveModal
        stage={stage}
        investigation={investigation}
        kind={externalKind}
        onDismiss={() => setExternalKind(null)}
        onError={onError}
      />

      <RedriveOptionsModal
        visible={optionsOpen}
        onDismiss={() => setOptionsOpen(false)}
        onChoose={chooseOption}
        disabledReason={optionDisabledReason}
        recommended={onDlqStep ? "dlq" : undefined}
        itemCount={investigation.assignedCount ?? 0}
        external={external}
      />

      <Modal
        visible={confirmReady}
        onDismiss={() => setConfirmReady(false)}
        header="Mark as ready to redrive?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmReady(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  markReady().catch(console.error);
                }}
                loading={update.isLoading}
              >
                Mark ready to redrive
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          <p>
            This tells the team the fix is in place and unlocks the redrive
            controls. The investigation moves from{" "}
            <strong>{statusLabel}</strong> to{" "}
            <strong>{STATUS_LABELS.READY_TO_REDRIVE}</strong>.
          </p>
        </TextContent>
      </Modal>
    </SpaceBetween>
  );
};

export default InvestigationRedrivePanel;
